import { DateOnly } from '@if/core/date'
import type {
  ExpectedPaymentAtDate,
  ExpectedPaymentsCalendar,
  FullLoan,
  Loan,
  LoanOverrideAmount,
  LoanOwnerHistory,
  LoanStatusHistory,
  Metro2Reporting,
  PaymentConfig,
} from '@src/types/loan/loan-calendar'
import type {
  ChangeOwnerOperation,
  CustomerPaymentOperation,
  InvoiceOperation,
  Operation,
  PayoutOperation,
  ProductRefundOperation,
} from '@src/types/loan/loan-operation'
import {
  AddFeeAction,
  CalendarSimulationAction as CalendarAction,
  CalendarSimulation,
  ChangePaymentStatusAction,
  ChangeRegularPmtAction,
  ComputeCalendarSimulationAction as ComputeCalendarAction,
  ECalendarSimulationAction,
  EditPaymentsCalendarAction,
  MoveCalendarCalendarAction,
  SignedCalendarSimulation,
  SpreadOverdueAmount,
  UpdatePaymentFrequencyCalendarAction,
} from '@src/types/loan/loan-simulation'

import { EMetro2OverrideReasons } from '@src/types/constants'
import {
  AddFeeActionDto,
  CalendarActionDto,
  ChangeOwnerOperationDto,
  ChangePaymentStatusActionDto,
  ChangeRegularPaymentActionDto,
  ComputeCalendarSimulationActionDto as ComputeCalendarActionDto,
  CustomerPaymentOperationDto,
  ECalendarActionDto,
  EditPaymentsCalendarActionDto,
  EOperationType,
  ExpectedPaymentAtDateDto,
  ExpectedPaymentsCalendarDto,
  FullLoanDto,
  InvoiceOperationDto,
  LoanDto,
  LoanOverrideAmountDto,
  LoanOwnerHistoryDto,
  LoanStatusHistoryDto,
  Metro2ReportingDto,
  MoveCalendarCalendarActionDto,
  OperationDto,
  PaymentConfigDto,
  PayoutOperationDto,
  ProductRefundOperationDto,
  SignedCalendarSimulationDto,
  SpreadOverdueAmountDto,
  UpdatePaymentFrequencyCalendarActionDto,
} from './loan-dto'

export function transformLoanFromDto(dto: LoanDto): Loan {
  return {
    ...dto,
    activationOn: new DateOnly(dto.activationOn),
    firstPaymentOn: new DateOnly(dto.firstPaymentOn),
    lastPaymentOn: new DateOnly(dto.lastPaymentOn),
  }
}

export function transformLoanOverrideAmountFromDto(dto: LoanOverrideAmountDto): LoanOverrideAmount {
  return {
    ...dto,
    effectiveFrom: new DateOnly(dto.effectiveFrom),
    effectiveTo: new DateOnly(dto.effectiveTo),
    createdOn: new Date(dto.createdOn),
  }
}

export function transformPaymentConfigFromDto(dto: PaymentConfigDto): PaymentConfig {
  return {
    ...dto,
    effectiveDate: new DateOnly(dto.effectiveDate),
    overrideAmounts: dto.overrideAmounts.map(transformLoanOverrideAmountFromDto),
  }
}

export function transformLoanStatusHistoryFromDto(dto: LoanStatusHistoryDto): LoanStatusHistory {
  return {
    ...dto,
    createdOn: new Date(dto.createdOn),
  }
}

export function transformExpectedPaymentAtDateFromDto(dto: ExpectedPaymentAtDateDto): ExpectedPaymentAtDate {
  return {
    ...dto,
    date: new DateOnly(dto.date),
  }
}

export function transformExpectedPaymentsCalendarFromDto(dto: ExpectedPaymentsCalendarDto): ExpectedPaymentsCalendar {
  return {
    ...dto,
    expectedPayments: dto.expectedPayments.map(transformExpectedPaymentAtDateFromDto),
  }
}

export function transformLoanOwnerHistoryFromDto(dto: LoanOwnerHistoryDto): LoanOwnerHistory {
  return {
    ...dto,
    effectiveDate: new DateOnly(dto.effectiveDate),
  }
}

export function transformFullLoanFromDto(dto: FullLoanDto): FullLoan {
  return {
    ...dto,
    loan: transformLoanFromDto(dto.loan),
    paymentConfigs: dto.paymentConfigs.map(transformPaymentConfigFromDto),
    statusHistory: dto.statusHistory.map(transformLoanStatusHistoryFromDto),
    expectedPaymentsCalendar: transformExpectedPaymentsCalendarFromDto(dto.expectedPaymentsCalendar),
    ownerHistory: dto.ownerHistory.map(transformLoanOwnerHistoryFromDto),
    metro2Reportings: dto.metro2Reportings?.map(transformMetro2ReportingFromDto),
  }
}

export function transformMetro2ReportingFromDto(dto: Metro2ReportingDto): Metro2Reporting {
  return {
    ...dto,
    metro2OverrideDate: new DateOnly(dto.metro2OverrideDate),
    createdOn: new Date(dto.createdOn),
    reason: dto.reason as EMetro2OverrideReasons,
  }
}

// Operation mappers
export function transformChangeOwnerOperationFromDto(dto: ChangeOwnerOperationDto): ChangeOwnerOperation {
  return {
    ...dto,
    effectiveDate: new DateOnly(dto.effectiveDate),
  }
}

export function transformCustomerPaymentOperationFromDto(dto: CustomerPaymentOperationDto): CustomerPaymentOperation {
  return {
    ...dto,
    effectiveDate: new DateOnly(dto.effectiveDate),
    paymentNumber: 0, // Will be set later in transformCalendarSimulationFromDto
  }
}

export function transformInvoiceOperationFromDto(dto: InvoiceOperationDto): InvoiceOperation {
  return {
    ...dto,
    effectiveDate: new DateOnly(dto.effectiveDate),
  }
}

export function transformPayoutOperationFromDto(dto: PayoutOperationDto): PayoutOperation {
  return {
    ...dto,
    effectiveDate: new DateOnly(dto.effectiveDate),
  }
}

export function transformProductRefundOperationFromDto(dto: ProductRefundOperationDto): ProductRefundOperation {
  return {
    ...dto,
    effectiveDate: new DateOnly(dto.effectiveDate),
  }
}

export function transformOperationFromDto(dto: OperationDto): Operation {
  switch (dto._internalType) {
    case EOperationType.ChangeOwnerOperation:
      return transformChangeOwnerOperationFromDto(dto)
    case EOperationType.CustomerPaymentOperation:
      return transformCustomerPaymentOperationFromDto(dto)
    case EOperationType.InvoiceOperation:
      return transformInvoiceOperationFromDto(dto)
    case EOperationType.PayoutOperation:
      return transformPayoutOperationFromDto(dto)
    case EOperationType.ProductRefundOperation:
      return transformProductRefundOperationFromDto(dto)
    default:
      throw new Error(`Unknown operation type: ${(dto as { _internalType?: string })?._internalType}`)
  }
}

// Reverse mappers - from domain objects to DTOs

export function transformLoanToDto(loan: Loan): LoanDto {
  return {
    ...loan,
    activationOn: loan.activationOn.toJSON(),
    firstPaymentOn: loan.firstPaymentOn.toJSON(),
    lastPaymentOn: loan.lastPaymentOn.toJSON(),
  }
}

export function transformLoanOverrideAmountToDto(loanOverrideAmount: LoanOverrideAmount): LoanOverrideAmountDto {
  return {
    ...loanOverrideAmount,
    effectiveFrom: loanOverrideAmount.effectiveFrom.toJSON(),
    effectiveTo: loanOverrideAmount.effectiveTo.toJSON(),
    createdOn: loanOverrideAmount.createdOn.toJSON(),
  }
}

export function transformPaymentConfigToDto(paymentConfig: PaymentConfig): PaymentConfigDto {
  return {
    ...paymentConfig,
    effectiveDate: paymentConfig.effectiveDate.toJSON(),
    overrideAmounts: paymentConfig.overrideAmounts.map(transformLoanOverrideAmountToDto),
  }
}

export function transformLoanStatusHistoryToDto(statusHistory: LoanStatusHistory): LoanStatusHistoryDto {
  return {
    ...statusHistory,
    createdOn: statusHistory.createdOn.toJSON(),
  }
}

export function transformExpectedPaymentAtDateToDto(expectedPayment: ExpectedPaymentAtDate): ExpectedPaymentAtDateDto {
  return {
    ...expectedPayment,
    date: expectedPayment.date.toJSON(),
  }
}

export function transformExpectedPaymentsCalendarToDto(
  calendar: ExpectedPaymentsCalendar,
): ExpectedPaymentsCalendarDto {
  return {
    ...calendar,
    expectedPayments: calendar.expectedPayments.map(transformExpectedPaymentAtDateToDto),
  }
}

export function transformLoanOwnerHistoryToDto(ownerHistory: LoanOwnerHistory): LoanOwnerHistoryDto {
  return {
    ...ownerHistory,
    effectiveDate: ownerHistory.effectiveDate.toJSON(),
  }
}

export function transformFullLoanToDto(fullLoan: FullLoan): FullLoanDto {
  return {
    ...fullLoan,
    loan: transformLoanToDto(fullLoan.loan),
    paymentConfigs: fullLoan.paymentConfigs.map(transformPaymentConfigToDto),
    statusHistory: fullLoan.statusHistory.map(transformLoanStatusHistoryToDto),
    expectedPaymentsCalendar: transformExpectedPaymentsCalendarToDto(fullLoan.expectedPaymentsCalendar),
    ownerHistory: fullLoan.ownerHistory.map(transformLoanOwnerHistoryToDto),
    metro2Reportings: fullLoan.metro2Reportings?.map(transformMetro2ReportingToDto),
  }
}

export function transformMetro2ReportingToDto(metro2Reporting: Metro2Reporting): Metro2ReportingDto {
  return {
    ...metro2Reporting,
    metro2OverrideDate: metro2Reporting.metro2OverrideDate.toJSON(),
    createdOn: metro2Reporting.createdOn.toJSON(),
    reason: metro2Reporting.reason as string,
  }
}

// Operation reverse mappers
export function transformChangeOwnerOperationToDto(operation: ChangeOwnerOperation): ChangeOwnerOperationDto {
  return {
    ...operation,
    effectiveDate: operation.effectiveDate.toJSON(),
  }
}

export function transformCustomerPaymentOperationToDto(
  operation: CustomerPaymentOperation,
): CustomerPaymentOperationDto {
  return {
    ...operation,
    effectiveDate: operation.effectiveDate.toJSON(),
  }
}

export function transformInvoiceOperationToDto(operation: InvoiceOperation): InvoiceOperationDto {
  return {
    ...operation,
    effectiveDate: operation.effectiveDate.toJSON(),
  }
}

export function transformPayoutOperationToDto(operation: PayoutOperation): PayoutOperationDto {
  return {
    ...operation,
    effectiveDate: operation.effectiveDate.toJSON(),
  }
}

export function transformProductRefundOperationToDto(operation: ProductRefundOperation): ProductRefundOperationDto {
  return {
    ...operation,
    effectiveDate: operation.effectiveDate.toJSON(),
  }
}

export function transformOperationToDto(operation: Operation): OperationDto {
  switch (operation._internalType) {
    case EOperationType.ChangeOwnerOperation:
      return transformChangeOwnerOperationToDto(operation)
    case EOperationType.CustomerPaymentOperation:
      return transformCustomerPaymentOperationToDto(operation)
    case EOperationType.InvoiceOperation:
      return transformInvoiceOperationToDto(operation)
    case EOperationType.PayoutOperation:
      return transformPayoutOperationToDto(operation)
    case EOperationType.ProductRefundOperation:
      return transformProductRefundOperationToDto(operation)
    default:
      throw new Error(`Unknown operation type: ${(operation as { _internalType?: string })?._internalType}`)
  }
}

export function transformSpreadOverdueAmountToDto(spreadOverdueAmount: SpreadOverdueAmount): SpreadOverdueAmountDto {
  return {
    paymentsDate: spreadOverdueAmount.paymentsDate,
    simulation: CalendarSimulation.toDto(spreadOverdueAmount.simulation),
  }
}

// Simulation mappers
export function transformUpdatePaymentFrequencyCalendarActionFromDto(
  dto: UpdatePaymentFrequencyCalendarActionDto,
): UpdatePaymentFrequencyCalendarAction {
  return {
    ...dto,
    actionType: ECalendarSimulationAction.UpdateFrequency,
    frequency: dto.frequency,
    firstPaymentDate: new DateOnly(dto.firstPaymentDate),
  }
}

export function transformMoveCalendarCalendarActionFromDto(
  dto: MoveCalendarCalendarActionDto,
): MoveCalendarCalendarAction {
  return {
    actionType: ECalendarSimulationAction.MoveCalendar,
    fromDate: new DateOnly(dto.fromDate),
    toDate: new DateOnly(dto.toDate),
    reason: dto.reason,
    paymentToRaiseIds: dto.paymentToRaiseIds,
  }
}

export function transformEditPaymentsCalendarActionFromDto(
  dto: EditPaymentsCalendarActionDto,
): EditPaymentsCalendarAction {
  return {
    actionType: ECalendarSimulationAction.EditPayments,
    editedPayments: dto.editedPayments.map((editedPayment) => ({
      operationId: editedPayment.operationId,
      effectiveDate: editedPayment.effectiveDate ? new DateOnly(editedPayment.effectiveDate) : undefined,
      amount: editedPayment.amount,
      paymentMethod: editedPayment.paymentMethod,
      note: editedPayment.note,
      sendSmsReminder: editedPayment.sendSmsReminder,
    })),
    newPayments: dto.newPayments.map((newPayment) => ({
      effectiveDate: new DateOnly(newPayment.effectiveDate),
      amount: newPayment.amount,
      paymentMethod: newPayment.paymentMethod,
      note: newPayment.note,
      sendSmsReminder: newPayment.sendSmsReminder,
    })),
    deletedPaymentIds: dto.deletedPaymentIds,
  }
}

export function transformChangePaymentStatusCalendarActionFromDto(
  dto: ChangePaymentStatusActionDto,
): ChangePaymentStatusAction {
  return {
    actionType: ECalendarSimulationAction.ChangePaymentStatus,
    paymentIds: dto.paymentIds,
    newStatus: dto.newStatus,
    transactionDate: dto.transactionDate ? new DateOnly(dto.transactionDate) : undefined,
    nsfFee: dto.nsfFee,
    nsfStatus: dto.nsfStatus,
  }
}

export function transformAddFeeActionFromDto(dto: AddFeeActionDto): AddFeeAction {
  return {
    actionType: ECalendarSimulationAction.AddFee,
    effectiveDate: new DateOnly(dto.effectiveDate),
    amount: dto.amount,
    feeType: dto.feeType,
    note: dto.note,
  }
}

export function transformChangeRegularPmtActionFromDto(dto: ChangeRegularPaymentActionDto): ChangeRegularPmtAction {
  return {
    actionType: ECalendarSimulationAction.ChangeRegularPmt,
    amount: dto.amount,
    fromDate: new DateOnly(dto.fromDate),
  }
}

export function transformCalendarActionFromDto(dto: CalendarActionDto): CalendarAction {
  switch (dto.actionType) {
    case ECalendarActionDto.UpdatePaymentFrequency:
      return transformUpdatePaymentFrequencyCalendarActionFromDto(dto)
    case ECalendarActionDto.MoveCalendar:
      return transformMoveCalendarCalendarActionFromDto(dto)
    case ECalendarActionDto.EditPayments:
      return transformEditPaymentsCalendarActionFromDto(dto)
    case ECalendarActionDto.Reset:
      return {
        ...dto,
        actionType: ECalendarSimulationAction.Reset,
      }
    case ECalendarActionDto.ChangePaymentStatus:
      return transformChangePaymentStatusCalendarActionFromDto(dto)
    case ECalendarActionDto.AddFee:
      return transformAddFeeActionFromDto(dto)
    case ECalendarActionDto.ChangeRegularPmt:
      return transformChangeRegularPmtActionFromDto(dto)
    default:
      throw new Error(`Unknown simulation action type: ${(dto as { actionType?: string })?.actionType}`)
  }
}

export function transformSignedCalendarSimulationFromDto(dto: SignedCalendarSimulationDto): SignedCalendarSimulation {
  return {
    ...dto,
    signature: dto.signature,
    simulation: CalendarSimulation.fromDto(dto.simulation),
  }
}

// Reverse simulation mappers
export function transformUpdatePaymentFrequencyCalendarActionToDto(
  action: UpdatePaymentFrequencyCalendarAction,
): UpdatePaymentFrequencyCalendarActionDto {
  return {
    ...action,
    actionType: ECalendarActionDto.UpdatePaymentFrequency,
    frequency: action.frequency,
    firstPaymentDate: action.firstPaymentDate.toJSON(),
  }
}

export function transformMoveCalendarCalendarActionToDto(
  action: MoveCalendarCalendarAction,
): MoveCalendarCalendarActionDto {
  return {
    actionType: ECalendarActionDto.MoveCalendar,
    fromDate: action.fromDate.toJSON(),
    toDate: action.toDate.toJSON(),
    reason: action.reason,
    paymentToRaiseIds: action.paymentToRaiseIds,
  }
}

export function transformEditPaymentsCalendarActionToDto(
  action: EditPaymentsCalendarAction,
): EditPaymentsCalendarActionDto {
  return {
    actionType: ECalendarActionDto.EditPayments,
    editedPayments: action.editedPayments.map((editedPayment) => ({
      operationId: editedPayment.operationId,
      effectiveDate: editedPayment.effectiveDate ? new DateOnly(editedPayment.effectiveDate).toString() : undefined,
      amount: editedPayment.amount,
      paymentMethod: editedPayment.paymentMethod,
      note: editedPayment.note,
      sendSmsReminder: editedPayment.sendSmsReminder,
    })),
    newPayments: action.newPayments.map((newPayment) => ({
      effectiveDate: new DateOnly(newPayment.effectiveDate).toString(),
      amount: newPayment.amount,
      paymentMethod: newPayment.paymentMethod,
      note: newPayment.note,
      sendSmsReminder: newPayment.sendSmsReminder,
    })),
    deletedPaymentIds: action.deletedPaymentIds,
  }
}

export function transformChangePaymentStatusCalendarActionToDto(
  action: ChangePaymentStatusAction,
): ChangePaymentStatusActionDto {
  return {
    actionType: ECalendarActionDto.ChangePaymentStatus,
    paymentIds: action.paymentIds,
    newStatus: action.newStatus,
    transactionDate: action.transactionDate ? action.transactionDate.toJSON() : undefined,
    nsfFee: action.nsfFee,
    nsfStatus: action.nsfStatus,
  }
}

export function transformAddFeeActionToDto(action: AddFeeAction): AddFeeActionDto {
  return {
    actionType: ECalendarActionDto.AddFee,
    effectiveDate: action.effectiveDate.toJSON(),
    amount: action.amount,
    feeType: action.feeType,
    note: action.note,
  }
}

export function transformChangeRegularPmtActionToDto(action: ChangeRegularPmtAction): ChangeRegularPaymentActionDto {
  return {
    actionType: ECalendarActionDto.ChangeRegularPmt,
    amount: action.amount,
    fromDate: action.fromDate.toJSON(),
  }
}

export function transformCalendarActionToDto(action: CalendarAction): CalendarActionDto {
  switch (action.actionType) {
    case ECalendarSimulationAction.UpdateFrequency:
      return transformUpdatePaymentFrequencyCalendarActionToDto(action)
    case ECalendarSimulationAction.MoveCalendar:
      return transformMoveCalendarCalendarActionToDto(action)
    case ECalendarSimulationAction.EditPayments:
      return transformEditPaymentsCalendarActionToDto(action)
    case ECalendarSimulationAction.Reset:
      return {
        ...action,
        actionType: ECalendarActionDto.Reset,
      }
    case ECalendarSimulationAction.ChangePaymentStatus:
      return transformChangePaymentStatusCalendarActionToDto(action)
    case ECalendarSimulationAction.AddFee:
      return transformAddFeeActionToDto(action)
    case ECalendarSimulationAction.ChangeRegularPmt:
      return transformChangeRegularPmtActionToDto(action)
    default:
      throw new Error(`Unknown simulation action type: ${(action as { actionType?: string })?.actionType}`)
  }
}

export function transformSignedCalendarSimulationToDto(
  simulation: SignedCalendarSimulation,
): SignedCalendarSimulationDto {
  return {
    ...simulation,
    signature: simulation.signature,
    simulation: CalendarSimulation.toDto(simulation.simulation),
  }
}

export function transformComputeCalendarActionToDto(action: ComputeCalendarAction): ComputeCalendarActionDto {
  return {
    ...action,
    action: transformCalendarActionToDto(action.action),
    signature: action.signature,
    simulation: action.simulation ? CalendarSimulation.toDto(action.simulation) : undefined,
  }
}
